<script lang="ts">
	import { format } from 'svelte-i18n';
	import { beforeUpdate } from 'svelte';
	import {
		get_z_wave_channel_structure_from_category,
		get_z_wave_ison,
		real_id,
	} from '../../../helpers/interface_helpers';
	import { unlockPin } from '../../../helpers/pin_lock_helpers';
	import { capFirstChar } from '../../../helpers/utilities';
	import { calculate_timer } from '../../../helpers/timer_helpers';
	import { hasControlRights } from 'src/helpers/user_helpers';
	import { zwaveBinarySwitchSet, zwaveLightSet } from '../../../services/xz_http_reqs';
	import { hapticFeedback } from '../../../services/native_container_requests_ios';
	import {
		devices,
		devices_status,
		devices_settings,
		os,
		auth_locked_devices,
		pin_unlocked_devices,
	} from '../../../stores';
	import PinUnlock from '../../PinUnlock.svelte';
	import { toast, DeviceSwitch } from '@Shelly/shelly-design-system';

	let className = '';
	export { className as class };

	export let status,
		device,
		settings,
		isWide = false,
		forceDisabled = false;

	$: status = $devices_status[real_id(device.id)];
	$: settings = $devices_settings[real_id(device.id)];

	let isLoading = false;
	let ison = false;
	let expected_ison = false;

	$: component = get_z_wave_channel_structure_from_category(device);
	$: componentStatus = status?.[component];
	$: isLightComponent = component.startsWith('light:');
	$: if (!isLoading) {
		ison = get_z_wave_ison(componentStatus);
	}

	let hasTimer = false;
	let timer = { time: undefined, percentage: undefined };
	let timerStarted;
	let cacheTimerStarted;

	let pinLocked;
	let showModal = false;

	$: hasRights = hasControlRights(device);

	beforeUpdate(() => {
		pinLocked =
			typeof $pin_unlocked_devices[device.id] === 'undefined' && $devices[device.id]?.pin_lock;

		if (status) {
			hasTimer =
				componentStatus?.duration?.value !== 0 &&
				typeof componentStatus?.duration?.value !== 'undefined';

			timerStarted = hasTimer ? componentStatus?.duration?.value : false;
			if (typeof cacheTimerStarted === 'undefined') {
				cacheTimerStarted = timerStarted;
			}
			if (timerStarted && hasTimer) {
				const [, time, percentage] = calculate_timer(device, status, settings);
				if (Object.values(timer).some((t) => typeof t === 'undefined')) {
					timer.time = time;
					timer.percentage = Number(percentage.toFixed(0));
				}
				if (cacheTimerStarted !== timerStarted) {
					cacheTimerStarted = timerStarted;
					timer.time = time;
					timer.percentage = Number(percentage.toFixed(0));
				}
			} else {
				timer.time = undefined;
				timer.percentage = undefined;
			}
		}
	});

	const handleTurn = async () => {
		if (pinLocked) {
			showModal = true;
			return;
		}

		if ($os === 'IOS') {
			hapticFeedback('buttonPressed');
		}
		if (isLoading) return;
		isLoading = true;

		expected_ison = !ison;

		const res = isLightComponent
			? await zwaveLightSet(device, { on: expected_ison })
			: await zwaveBinarySwitchSet(device, expected_ison);

		if (res.isok) {
			ison = expected_ison;
			const nextStatus = { ...(status || {}) };
			const nextComponent = {
				...(nextStatus[component] || {}),
			};
			if (typeof nextComponent.currentValue !== 'undefined') {
				nextComponent.currentValue = expected_ison;
			} else if (typeof nextComponent.output !== 'undefined') {
				nextComponent.output = expected_ison;
			} else {
				nextComponent.output = expected_ison;
			}
			nextStatus[component] = nextComponent;

			devices_status.update_device_status({
				id: real_id(device.id),
				msg: nextStatus,
			});
		} else if ($auth_locked_devices.indexOf(real_id(device.id)) == -1) {
			toast.open({ type: 'danger', title: $format('error_global') });
		}
		isLoading = false;
	};

	const handlePin = () => {
		pinLocked = unlockPin(device, pinLocked);
		handleTurn();
	};

	const handleClick = (e: Event) => {
		e.stopPropagation();
		e.preventDefault();
		if (pinLocked) {
			showModal = true;
			return;
		}
		handleTurn();
	};

	const a11yProps = {
		timerAriaLabel: $format('label_turn_device', { default: 'Turn device' }),
	};
</script>

{#key timerStarted || hasTimer}
	{#if isWide}
		<div class="d-flex flex-direction-column align-items-center justify-content-center height-full">
			<DeviceSwitch
				id="devicebutton-zw-{device.id}"
				value={ison
					? capFirstChar($format('on', { default: 'on' }))
					: capFirstChar($format('off', { default: 'off' }))}
				aria-label={$format('label_turn_device', { default: 'Turn device' })}
				{a11yProps}
				{isLoading}
				{hasTimer}
				{timer}
				checked={ison}
				variant="primary"
				on:click={handleClick}
				disabled={!status?.online || !hasRights || forceDisabled}
				class={className}
			/>
		</div>
	{:else}
		<DeviceSwitch
			id="devicebutton-zw-{device.id}"
			value={ison
				? capFirstChar($format('on', { default: 'on' }))
				: capFirstChar($format('off', { default: 'off' }))}
			aria-label={$format('label_turn_device', { default: 'Turn device' })}
			{a11yProps}
			{isLoading}
			{hasTimer}
			{timer}
			checked={ison}
			isActive={ison}
			on:click={handleClick}
			disabled={!status?.online || !hasRights || forceDisabled}
			class={className}
		/>
	{/if}
{/key}

{#if showModal}
	<PinUnlock bind:showModal on:unlock={handlePin} on:cancel={() => (showModal = false)} />
{/if}
